import { BsHandbag } from "react-icons/bs";
import { BiUser, BiHeart, BiSearch, BiChevronRight } from "react-icons/bi";
import { IoCloseOutline } from "react-icons/io5";
import { HiMenuAlt4 } from "react-icons/hi";
import React, { useState, useEffect } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/auth";
import { useCart } from "../../context/cart";
import { useSearch } from "../../context/search";
import toast from "react-hot-toast";
import axios from "axios";
import { FiLogOut } from "react-icons/fi";

const Header = () => {
  const [auth, setAuth] = useAuth();
  const [cart] = useCart();
  const [values, setValues] = useSearch();
  const [menuOpen, setMenuOpen] = useState(false);
  const [hideNav, setHideNav] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  // Check if search should be visible
  const showSearch =
    location.pathname === "/" || location.pathname === "/search";

  const handleLogout = () => {
    setAuth({ ...auth, user: null, token: "" });
    localStorage.removeItem("auth");
    toast.success("Logout Successfully");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!values.keyword.trim()) return;

    try {
      const { data } = await axios.get(
        `/api/v1/product/search/${values.keyword}`,
      );
      setValues({ ...values, results: data });
      navigate("/search");
    } catch (error) {
      console.log(error);
      toast.error("Something went wrong in search");
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setHideNav(currentScrollY > 80);
      setScrolled(currentScrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "auto";
  }, [menuOpen]);

  const logoBlack = "/images/logo-b.png";

  const goToDashboard = () => {
    navigate(`/dashboard/${auth?.user?.role === 1 ? "admin" : "user"}`);
  };

  return (
    <>
      <nav
        className={`
        fixed top-0 z-40 w-full transition-all duration-500 ease-in-out
        ${hideNav ? "-translate-y-full" : "translate-y-0"}
        ${
          scrolled ? "bg-white/90 backdrop-blur-xl shadow-sm" : "bg-transparent"
        }
      `}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-[70px] sm:h-20 gap-4">
            <Link
              to="/"
              className="flex-shrink-0 flex items-center gap-2 transition-opacity hover:opacity-80"
            >
              <img
                className="h-8 w-auto sm:h-10"
                src={logoBlack}
                alt="Russberry Designs"
              />
              <span
                className="hidden sm:flex leading-none text-2xl items-center"
                style={{ fontFamily: "'TAN PEARL', serif" }}
              >
                Russh Berry
              </span>
            </Link>

            <div className="flex-1 max-w-sm mx-2 lg:mx-12 h-10">
              {showSearch && (
                <form
                  role="search"
                  onSubmit={handleSubmit}
                  className="group relative w-full"
                >
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <BiSearch className="text-neutral-400 text-xl group-focus-within:text-black transition-colors" />
                  </div>
                  <input
                    type="text"
                    placeholder="Search products..."
                    value={values.keyword}
                    onChange={(e) =>
                      setValues({ ...values, keyword: e.target.value })
                    }
                    className="
                    block w-full pl-10 pr-3 py-2.5 
                    bg-transparent border border-neutral-500 rounded-full 
                    text-sm font-medium text-neutral-900 placeholder-neutral-500
                    focus:outline-none focus:border-black focus:ring-0
                    transition-all duration-300 ease-out
                  "
                  />
                </form>
              )}
            </div>

            <div className="hidden lg:flex items-center gap-6">
              <NavLink
                to="/wishlist"
                className={({ isActive }) =>
                  `group relative p-2 rounded-full transition-all duration-300 ${
                    isActive
                      ? "bg-neutral-100 text-black"
                      : "text-neutral-600 hover:bg-neutral-50 hover:text-black"
                  }`
                }
              >
                <BiHeart className="text-2xl" />
              </NavLink>

              {!auth?.user ? (
                <NavLink
                  to="/login"
                  className={({ isActive }) =>
                    `group relative p-2 rounded-full transition-all duration-300 ${
                      isActive
                        ? "bg-neutral-100 text-black"
                        : "text-neutral-600 hover:bg-neutral-50 hover:text-black"
                    }`
                  }
                >
                  <BiUser className="text-2xl" />
                </NavLink>
              ) : (
                <button
                  onClick={goToDashboard}
                  className="p-2 rounded-full text-neutral-600 hover:bg-neutral-50 hover:text-black transition-all"
                >
                  <BiUser className="text-2xl" />
                </button>
              )}

              <NavLink
                to="/cart"
                className={({ isActive }) =>
                  `group relative p-2 rounded-full transition-all duration-300 ${
                    isActive
                      ? "bg-neutral-100 text-black"
                      : "text-neutral-600 hover:bg-neutral-50 hover:text-black"
                  }`
                }
              >
                <BsHandbag className="text-2xl" />
                {cart?.length > 0 && (
                  <span className="absolute top-0 right-0 h-4 w-4 bg-black text-white text-[10px] font-bold flex items-center justify-center rounded-full ring-2 ring-white">
                    {cart.length}
                  </span>
                )}
              </NavLink>
            </div>

            <div className="lg:hidden flex items-center">
              <button
                onClick={() => setMenuOpen(!menuOpen)}
                className="relative p-2 -mr-2 text-neutral-800 focus:outline-none rounded-full hover:bg-neutral-100 transition-colors"
              >
                <HiMenuAlt4 className="text-3xl" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div
        className={`
          fixed inset-0 z-50 bg-black/20 backdrop-blur-[2px] transition-opacity duration-500 lg:hidden
          ${menuOpen ? "opacity-100 visible" : "opacity-0 invisible"}
        `}
        onClick={() => setMenuOpen(false)}
      />

      <div
        className={`
          fixed top-0 right-0 z-50 h-full w-[85%] sm:w-[380px] bg-white 
          shadow-[-10px_0_30px_rgba(0,0,0,0.05)] transform transition-transform duration-500 cubic-bezier(0.33, 1, 0.68, 1) lg:hidden
          ${menuOpen ? "translate-x-0" : "translate-x-full"}
        `}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between pt-2 pb-2 px-6 bg-neutral-50 ">
            <div className="flex-1">
              {!auth?.user ? (
                <Link
                  to="/login"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center gap-3 group"
                >
                  <div className="h-10 w-10 rounded-full bg-neutral-200 flex items-center justify-center group-hover:scale-105 transition-transform">
                    <BiUser className="text-xl text-neutral-500" />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-neutral-900">
                      Welcome
                    </h3>
                    <p className="text-xs text-neutral-500">Sign in</p>
                  </div>
                </Link>
              ) : (
                <div
                  onClick={() => {
                    goToDashboard();
                    setMenuOpen(false);
                  }}
                  className="flex items-center gap-3 cursor-pointer"
                >
                  <div className="h-10 w-10 rounded-full bg-black text-white flex items-center justify-center text-base font-bold">
                    {auth.user.name[0]}
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-neutral-900">
                      {auth.user.name}
                    </h3>
                    <p className="text-xs text-neutral-500 truncate max-w-[120px]">
                      {auth.user.email}
                    </p>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => setMenuOpen(false)}
              className="p-2 -mr-2 rounded-full text-neutral-400 hover:text-black hover:bg-neutral-200/50 transition-all"
            >
              <IoCloseOutline className="text-2xl" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="flex flex-col space-y-1">
              <NavLink
                to="/wishlist"
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center justify-between p-4 rounded-xl transition-all duration-200 ${
                    isActive
                      ? "bg-neutral-100 text-black font-semibold"
                      : "text-neutral-600 hover:bg-neutral-50 hover:pl-6"
                  }`
                }
              >
                <div className="flex items-center gap-4">
                  <BiHeart className="text-xl" />
                  <span className="text-base">Wishlist</span>
                </div>
                <BiChevronRight className="text-neutral-400 text-xl" />
              </NavLink>

              <NavLink
                to="/cart"
                onClick={() => setMenuOpen(false)}
                className={({ isActive }) =>
                  `flex items-center justify-between p-4 rounded-xl transition-all duration-200 ${
                    isActive
                      ? "bg-neutral-100 text-black font-semibold"
                      : "text-neutral-600 hover:bg-neutral-50 hover:pl-6"
                  }`
                }
              >
                <div className="flex items-center gap-4">
                  <BsHandbag className="text-xl" />
                  <span className="text-base">My Bag</span>
                </div>
                <div className="flex items-center gap-2">
                  {cart?.length > 0 && (
                    <span className="bg-black text-white text-[10px] font-bold px-2 py-1 rounded-full">
                      {cart.length}
                    </span>
                  )}
                  <BiChevronRight className="text-neutral-400 text-xl" />
                </div>
              </NavLink>

              {auth?.user && (
                <button
                  onClick={() => {
                    goToDashboard();
                    setMenuOpen(false);
                  }}
                  className="flex items-center justify-between p-4 rounded-xl text-neutral-600 hover:bg-neutral-50 hover:pl-6 transition-all duration-200 w-full text-left"
                >
                  <div className="flex items-center gap-4">
                    <BiUser className="text-xl" />
                    <span className="text-base">Dashboard</span>
                  </div>
                  <BiChevronRight className="text-neutral-400 text-xl" />
                </button>
              )}

              {auth?.user && (
                <button
                  onClick={handleLogout}
                  className="w-full mt-8 flex items-center justify-center gap-3 py-3 px-4 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-900 font-semibold transition-all duration-200"
                >
                  <FiLogOut size={20} />
                  Logout
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
